import { useCallback, useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { theme } from '@/design-system';
import { PagePaths } from '@/routes/page-paths';
import { Permission, useCan } from '@/shared/modules/permissions';
import { useEntitySlice } from '@/stores/selectors';

import { REVIEW_STEP_PREFIXES } from './common';
import { isEntityInReapprovalState } from './utils/entity-review-routing';

const DIRECTORS_STEP = 2;
const SUMMARY_STEP = 5;

export const useEntityReviewMode = () => {
  const { pathname } = useLocation();
  const canReviewCrossEntity = useCan(Permission.GlobalAdminCrossEntityAccess);
  const {
    stepForms,
    approvedStepForms,
    isViewingNewVersion,
    setIsViewingNewVersion,
    changedFieldPaths,
    changedCountByStep,
    changedDirectorIds,
  } = useEntitySlice();

  const isOnReviewRoute = Boolean(matchPath(PagePaths.manageEntityReview, pathname));

  const isReviewMode =
    isOnReviewRoute &&
    canReviewCrossEntity &&
    isEntityInReapprovalState(stepForms) &&
    Boolean(approvedStepForms);

  const forms = isViewingNewVersion ? stepForms : (approvedStepForms ?? stepForms);

  const highlightColor = isViewingNewVersion
    ? theme.colors.warning.solid
    : theme.colors.border.focus;

  const changedStepColors = isViewingNewVersion
    ? { active: theme.colors.warning.solid, inactive: theme.colors.warning.subtle }
    : { active: theme.colors.border.focus, inactive: theme.colors.border.focus };

  const changedPathsSet = useMemo(() => new Set(changedFieldPaths), [changedFieldPaths]);
  const changedDirectorSet = useMemo(() => new Set(changedDirectorIds), [changedDirectorIds]);

  const isChanged = useCallback(
    (path: string) => isReviewMode && changedPathsSet.has(path),
    [isReviewMode, changedPathsSet],
  );

  const isChangedUnder = useCallback(
    (prefix: string) =>
      isReviewMode &&
      changedFieldPaths.some(
        (path) => path === prefix || path.startsWith(`${prefix}.`) || path.startsWith(`${prefix}[`),
      ),
    [isReviewMode, changedFieldPaths],
  );

  const isDirectorsStepChanged =
    isReviewMode && (changedDirectorIds.length > 0 || forms.invitations.length > 0);

  const isStepChanged = useCallback(
    (step: number) =>
      isReviewMode &&
      (step === DIRECTORS_STEP ? isDirectorsStepChanged : Boolean(changedCountByStep[step])),
    [isReviewMode, changedCountByStep, isDirectorsStepChanged],
  );

  const changedStepsForUi = useMemo(
    () =>
      isReviewMode
        ? Object.keys(REVIEW_STEP_PREFIXES)
            .map(Number)
            .filter((step) => step !== SUMMARY_STEP && isStepChanged(step))
        : [],
    [isReviewMode, isStepChanged],
  );

  const validatedStepsForUi = useMemo(
    () => (isReviewMode ? Object.keys(REVIEW_STEP_PREFIXES).map(Number) : null),
    [isReviewMode],
  );

  const highlightIf = useCallback(
    (isMarked: boolean) => (isMarked ? highlightColor : undefined),
    [highlightColor],
  );

  const isDirectorChanged = useCallback(
    (userId?: string | null) =>
      isReviewMode && Boolean(userId) && changedDirectorSet.has(userId as string),
    [isReviewMode, changedDirectorSet],
  );

  return {
    isReviewMode,
    isOnReviewRoute,
    canReviewCrossEntity,
    isViewingNewVersion,
    setIsViewingNewVersion,
    forms,
    highlightColor,
    changedStepColors,
    highlightIf,
    isChanged,
    isChangedUnder,
    isStepChanged,
    isDirectorsStepChanged,
    changedStepsForUi,
    validatedStepsForUi,
    isDirectorChanged,
  };
};
